import React from 'react';
import {useTranslation} from 'react-i18next';

import {CaretDown, Check} from '../icons';
import {LearnTask} from './LearnTask';
import {percentOf} from './state/learnModel';
import {trackTitle} from './learnText';

/**
 * Una pista plegable: promesa, «4 de 6 · unos 6 min», omitidas y la barra. Lo esencial abre por
 * defecto; las demás, plegadas.
 *
 * La barra (`role="progressbar"`) va FUERA del botón de la cabecera y se dibuja encima con el CSS:
 * un `<button>` vuelve presentacional todo lo que tiene adentro, y el lector de pantalla no la vería
 * como barra. Por eso el botón le reserva el lugar (`.ld-track__slot`) y la barra no toma clics.
 */
export function LearnTrack({track, model, position, open, isProfile, agencyName, openTask, saving, justDone, onToggle,
                               onToggleTask, actions}) {
    const {t} = useTranslation();
    const title = trackTitle(t, track.key);
    const bodyId = `ld-track-${track.key}`;
    const progress = t('learn_dashbo.track.progress', {done: track.done, total: track.total});
    const meta = [progress];
    if (!track.achieved && track.minutesLeft > 0) {
        meta.push(t('learn_dashbo.track.minutes_left', {count: track.minutesLeft}));
    }
    if (track.skipped > 0) meta.push(t('learn_dashbo.track.skipped', {count: track.skipped}));

    return (
        <section className={`ld-track -${track.key}${open ? ' -open' : ''}${track.achieved ? ' -achieved' : ''}`}>
            <div className="ld-track__top">
                <h2 className="ld-track__heading">
                    <button type="button" className="ld-track__head" aria-expanded={open} aria-controls={bodyId}
                            onClick={onToggle}>
                        <span className="ld-track__badge" aria-hidden="true">
                            {track.achieved ? <Check size={16} weight="bold"/> : position}
                        </span>
                        <span className="ld-track__titles">
                            <span className="ld-track__title">{title}</span>
                            <span className="ld-track__promise">
                                {t(`learn_dashbo.tracks.${track.key}.promise`)}
                                {isProfile && ` · ${t('learn_dashbo.track.your_profile')}`}
                            </span>
                        </span>
                        <span className="ld-track__meta">{meta.join(' · ')}</span>
                        <span className="ld-track__slot" aria-hidden="true"/>
                        <CaretDown size={14} className="ld-track__caret" aria-hidden="true"/>
                    </button>
                </h2>
                <div className="ld-bar ld-track__bar" role="progressbar" aria-label={title}
                     aria-valuemin={0} aria-valuemax={track.total} aria-valuenow={track.done}
                     aria-valuetext={t('learn_dashbo.track.progress_text', {done: track.done, total: track.total})}>
                    <span className="ld-bar__fill" style={{width: `${percentOf(track.done, track.total)}%`}}/>
                </div>
            </div>

            {open && (
                <ul id={bodyId} className="ld-track__tasks">
                    {track.tasks.map((task) => (
                        <LearnTask key={task.key} task={task} model={model} agencyName={agencyName}
                                   open={openTask === task.key} saving={saving.has(task.key)}
                                   justDone={justDone.includes(task.key)}
                                   start={actions.startOf(task)}
                                   onToggle={() => onToggleTask(task.key)}
                                   onSkip={() => actions.skip(task.key)}
                                   onUnskip={() => actions.unskip(task.key)}
                                   onComplete={() => actions.complete(task.key)}
                                   onUncomplete={() => actions.uncomplete(task.key)}/>
                    ))}
                </ul>
            )}
        </section>
    );
}
